const DEFAULT_PIXELS_PER_SECOND = 50;
const REBUILD_DELAY_MILLISECONDS = 150;

/**
 * Ticker tape above the site header.
 *
 * The first message group is the only one exposed to assistive technology.
 * It is repeated until one half of the track covers the host, then the half
 * is repeated once more, so the -50% keyframe lands on a group boundary and
 * the loop has no seam. Duration is derived from the measured half width,
 * which keeps the pixel rate constant however long the messages are.
 */
class KbTicker extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #rebuildTimeout = undefined;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #group = null;

  #lastHostWidth = 0;

  #reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  connectedCallback() {
    this.#track = this.querySelector('[data-ticker-track]');
    this.#group = this.querySelector('[data-ticker-group]');

    if (!this.#track || !this.#group) {
      return;
    }

    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    this.#resizeObserver = new ResizeObserver((entries) => {
      const hostWidth = Math.round(
        entries[0]?.contentRect.width ?? 0
      );

      if (hostWidth === this.#lastHostWidth) {
        return;
      }

      this.#scheduleRebuild();
    });

    this.#resizeObserver.observe(this);

    this.#reducedMotionQuery.addEventListener(
      'change',
      () => this.#rebuild(),
      { signal }
    );

    document.fonts?.ready.then(() => {
      if (this.isConnected) {
        this.#rebuild();
      }
    });

    const headerGroup = this.closest('#header-group');

    headerGroup?.addEventListener(
      'site-header:state-change',
      (event) => this.#handleHeaderStateChange(event),
      { signal }
    );

    this.#setCollapsed(
      Boolean(
        headerGroup
          ?.querySelector('site-header')
          ?.classList.contains('is-mobile-menu-open')
      )
    );

    this.#rebuild();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#rebuildTimeout);
  }

  #scheduleRebuild() {
    clearTimeout(this.#rebuildTimeout);

    this.#rebuildTimeout = setTimeout(
      () => this.#rebuild(),
      REBUILD_DELAY_MILLISECONDS
    );
  }

  #rebuild() {
    const track = this.#track;
    const group = this.#group;

    if (!track || !group) {
      return;
    }

    for (const clone of track.querySelectorAll('[data-ticker-clone]')) {
      clone.remove();
    }

    const hostWidth = this.getBoundingClientRect().width;
    const groupWidth = group.getBoundingClientRect().width;

    this.#lastHostWidth = Math.round(hostWidth);

    if (
      this.#reducedMotionQuery.matches ||
      hostWidth === 0 ||
      groupWidth === 0
    ) {
      this.removeAttribute('data-ticker-ready');
      return;
    }

    const copiesPerHalf = Math.max(
      1,
      Math.ceil(hostWidth / groupWidth)
    );

    const clones = document.createDocumentFragment();

    for (
      let copyIndex = 1;
      copyIndex < copiesPerHalf * 2;
      copyIndex += 1
    ) {
      clones.append(this.#createClone(group));
    }

    track.append(clones);

    const pixelsPerSecond =
      Number(this.dataset.speed) || DEFAULT_PIXELS_PER_SECOND;

    const halfTrackWidth = groupWidth * copiesPerHalf;

    this.style.setProperty(
      '--kb-ticker-duration',
      `${(halfTrackWidth / pixelsPerSecond).toFixed(2)}s`
    );

    this.setAttribute('data-ticker-ready', '');
  }

  /**
   * @param {HTMLElement} group
   * @returns {HTMLElement}
   */
  #createClone(group) {
    const clone = /** @type {HTMLElement} */ (group.cloneNode(true));

    clone.removeAttribute('data-ticker-group');
    clone.setAttribute('data-ticker-clone', '');
    clone.setAttribute('aria-hidden', 'true');

    // Clones stay clickable, because the copy under the cursor is usually a
    // clone. They are only taken out of the tab order and the accessibility
    // tree, which inert would also do but at the cost of blocking clicks.
    for (const link of clone.querySelectorAll('a')) {
      link.setAttribute('tabindex', '-1');
    }

    for (const editorBlock of clone.querySelectorAll(
      '[data-shopify-editor-block]'
    )) {
      editorBlock.removeAttribute('data-shopify-editor-block');
    }

    return clone;
  }

  /**
   * @param {Event} event
   */
  #handleHeaderStateChange(event) {
    const detail =
      /** @type {CustomEvent<{ isMobileMenuOpen?: boolean }>} */ (event)
        .detail;

    this.#setCollapsed(Boolean(detail?.isMobileMenuOpen));
  }

  /**
   * @param {boolean} isCollapsed
   */
  #setCollapsed(isCollapsed) {
    this.classList.toggle('is-collapsed', isCollapsed);

    this.setAttribute(
      'aria-hidden',
      isCollapsed ? 'true' : 'false'
    );

    this.inert = isCollapsed;
  }
}

if (!customElements.get('kb-ticker')) {
  customElements.define('kb-ticker', KbTicker);
}
